const newSessionId = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  // eslint-disable-next-line no-bitwise
  bytes[6] = (bytes[6] & 15) | 64;
  // eslint-disable-next-line no-bitwise
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// Retain the exact request after an uncertain response. The server deduplicates
// it by session and sequence, including when the original save already committed.
export default function createChallengeSession({
  send, getUrl, onChange, makeId = newSessionId,
}) {
  let session = null;
  let pending = null;
  let inFlight = null;
  let stopping = null;
  let status = { active: false, saving: false, error: null };
  const publish = (patch) => {
    status = { ...status, ...patch };
    onChange(status);
  };

  const transmit = () => {
    if (inFlight) return inFlight;
    const request = pending;
    publish({ saving: true, error: null });
    inFlight = Promise.resolve().then(() => send(session.url, request)).then((response) => {
      if (!response || response.sequence !== request.sequence
        || response.stopped !== request.stop) {
        throw new Error('The server did not confirm the saved results.');
      }
      session.sequence = request.sequence;
      pending = null;
      if (response.stopped) publish({ active: false });
      return response;
    }).catch((error) => {
      publish({ error: error.message || 'Results could not be saved.' });
      throw error;
    })
      .finally(() => {
        inFlight = null;
        publish({ saving: Boolean(stopping) });
      });
    return inFlight;
  };

  const start = () => {
    if (status.active) {
      if (session.url !== getUrl()) throw new Error('Save the previous Challenge before switching games.');
      return session.id;
    }
    if (inFlight || stopping) throw new Error('Wait for results to finish saving.');
    session = { id: makeId(), sequence: 0, url: getUrl() };
    pending = null;
    publish({ active: true, saving: false, error: null });
    return session.id;
  };

  const saveAnswer = (answer) => {
    if (!status.active || stopping || pending || inFlight || status.error) {
      return Promise.reject(new Error('Finish saving the previous Challenge answer first.'));
    }
    pending = {
      session_id: session.id,
      sequence: session.sequence + 1,
      answer: { ...answer },
      stop: false,
    };
    return transmit();
  };

  const stop = () => {
    if (stopping) return stopping;
    if (!status.active) return Promise.resolve(null);
    publish({ saving: true });
    stopping = Promise.resolve().then(async () => {
      // An in-flight failure is surfaced, not silently retried. A subsequent
      // explicit Stop retries the retained request before closing the session.
      if (inFlight) await inFlight;
      if (pending) await transmit();
      if (!status.active) return null;
      pending = {
        session_id: session.id, sequence: session.sequence + 1, answer: null, stop: true,
      };
      return transmit();
    }).finally(() => {
      stopping = null;
      publish({ saving: false });
    });
    return stopping;
  };

  const retry = () => {
    if (stopping) return stopping;
    return pending ? transmit() : Promise.resolve(null);
  };

  return {
    start, saveAnswer, stop, retry,
  };
}
